import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Banner,
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
  createStyles,
  makeStyles,
  Theme,
  PrimaryButton,
  DialogActions,
  DefaultButton,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@bb-ui/react-library';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { useSnackbar } from 'hooks/useSnackbar';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { LearnConfigDetails } from 'App.types';
import { useAuthorization } from 'hooks/useAuthorization';
import { Invisible, Visible } from '@bb-ui/icons/dist/small';
import { isEqual } from 'lodash';
import { getVariantFleetDomain, isEmptyConfigValue, mergeConfigLayers } from 'utils/configVariants';

// The tenancy config schema now sends `type` as an array to signal optional primitives (e.g. ['string', 'null']). Converting these type arrays to a single type allows RJSF to render them without errors
const normalizeSchemaTypes = (schema: any): any => {
  if (!schema || typeof schema !== 'object') {
    return schema;
  }

  if (Array.isArray(schema)) {
    return schema.map((entry) => normalizeSchemaTypes(entry));
  }

  const clone: Record<string, any> = { ...schema };

  if (Array.isArray(clone.type)) {
    const [firstType] = clone.type;
    clone.type = clone.type.includes('string') ? 'string' : firstType;
  }

  if (clone.properties && typeof clone.properties === 'object') {
    clone.properties = Object.fromEntries(
      Object.entries(clone.properties).map(([key, value]) => [key, normalizeSchemaTypes(value)]),
    );
  }

  if (clone.additionalProperties && typeof clone.additionalProperties === 'object') {
    clone.additionalProperties = normalizeSchemaTypes(clone.additionalProperties);
  }

  if (clone.items && typeof clone.items === 'object') {
    clone.items = normalizeSchemaTypes(clone.items);
  }

  return clone;
};

const styles = (theme: Theme) =>
  createStyles({
    dialogTitle: {
      backgroundColor: theme.palette.background.b2,
    },
    dialogDescription: {
      borderBottom: `1px solid ${theme.palette.border.main}`,
      paddingLeft: '1rem',
      paddingBottom: '5px',
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.background.b2,
    },
    updateConfigInput: {
      margin: theme.spacing(1, 0, 1),
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: '0.75rem',
    },
    switchLabel: {
      marginBottom: 0,
    },
    switchBase: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&$checked:hover': {
        backgroundColor: 'transparent',
      },
    },
    checked: {},
    variantChip: {
      height: 18,
      marginLeft: theme.spacing(1),
      fontWeight: theme.typography.fontWeightBold,
    },
    track: {
      borderRadius: '1rem',
      width: '2.5rem',
      height: '1.5rem',
      opacity: 1,
      backgroundColor: 'red',
      '&$checked + &': {
        backgroundColor: 'red',
      },
    },
    iconWrapper: {
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.action.disabledBackground,
      '$checked &': {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
        marginRight: '-0.0625rem',
      },
    },
  });

export const useStyles = makeStyles(styles);

export interface UpdateConfigurationDialogProps extends DialogProps {
  selectedDomain: string;
  loading?: boolean;
  error?: boolean;
  uiSchema?: Record<string, any>;
  schema: Record<string, any>;
  newDomain: boolean;
  formData?: Record<string, any>;
  onClose: () => void;
  loadingAutofill?: boolean;
  onSuccess: () => void;
  tenantId: string;
  fleetConfig?: LearnConfigDetails[];
  tenantType: string;
  variants?: string[];
}

export const ActionsUpdateConfigurationDialog: React.FunctionComponent<
  UpdateConfigurationDialogProps
> = (props) => {
  const {
    id,
    open,
    onClose,
    selectedDomain,
    schema,
    uiSchema,
    error,
    loading,
    newDomain,
    formData,
    loadingAutofill,
    onSuccess,
    tenantId,
    fleetConfig,
    tenantType,
    variants,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles(props);
  const [fleetRevertConfirmationOpen, setFleetRevertConfirmationOpen] =
    React.useState<boolean>(false);
  const [shouldDecryptValues, setShouldDecryptValues] = React.useState(false);
  const [configData, setConfigData] = React.useState<Record<string, any>>(formData || {});
  const { fetch: decryptConfigFetch, data: decryptedConfig } = useRestApi(
    apiUrl('config', `tenants/${tenantId}/capabilityConfigs/${selectedDomain}?decrypt=true`),
    { manual: true },
  );
  const decryptedFormData = React.useMemo<LearnConfigDetails>(
    () => decryptedConfig ?? [],
    [decryptedConfig],
  );
  const hasFetchedDecrypted = React.useRef(false);

  interface TenantStateData {
    updated: number;
    LockId: string;
    created: number;
    TenantId: string;
    State: string;
    Locked: boolean;
  }

  const { enqueueSnackbar } = useSnackbar();
  const {
    clearFailedRequests,
    clearSucceededRequests,
    doPost,
    doPut,
    loadingRequests,
    failedRequests,
    succeededRequests,
  } = useRestApi(apiUrl('config', `tenants/${tenantId}/capabilityConfigs`), { manual: true });

  const { hasPermission } = useAuthorization();

  const canRestoreToFleetConfiguration = hasPermission('blackboard.sites', 'update');

  const fleetBaseDomainData = fleetConfig?.find(
    (item) => item.domain === `${selectedDomain}-fleet`,
  );
  const fleetVariantConfigs: Record<string, any>[] = [];
  const variantByKey: Record<string, string> = {};
  for (const variant of variants ?? []) {
    const variantDomainData = fleetConfig?.find(
      (item) => item.domain === getVariantFleetDomain(selectedDomain, variant),
    );
    if (variantDomainData) {
      fleetVariantConfigs.push(variantDomainData.configuration ?? {});
      for (const [key, value] of Object.entries(variantDomainData.configuration ?? {})) {
        if (!isEmptyConfigValue(value)) {
          variantByKey[key] = variant;
        }
      }
    }
  }

  const fleetConfigDomainData: LearnConfigDetails | null =
    fleetBaseDomainData || fleetVariantConfigs.length > 0
      ? ({
          ...fleetBaseDomainData,
          domain: `${selectedDomain}-fleet`,
          configuration: mergeConfigLayers(fleetBaseDomainData?.configuration, fleetVariantConfigs),
        } as LearnConfigDetails)
      : null;

  const variantChip = (variant: string) => (
    <Chip
      key={variant}
      className={classes.variantChip}
      label={t(`siteConfiguration.variants.${variant}`, { defaultValue: variant })}
      size="small"
    />
  );

  const variantValues = mergeConfigLayers({}, fleetVariantConfigs);
  const variantValuesKey = JSON.stringify(variantValues);

  const normalizedSchema = React.useMemo(() => {
    const normalized = normalizeSchemaTypes(schema);
    const extraValues = newDomain
      ? { ...(formData || {}) }
      : { ...JSON.parse(variantValuesKey), ...(formData || {}) };

    if (Object.keys(extraValues).length > 0) {
      const existingProps = normalized.properties || {};
      const missingProps: Record<string, any> = {};

      Object.keys(extraValues).forEach((key) => {
        if (!existingProps[key]) {
          const value = extraValues[key];
          const valueType = typeof value;

          if (valueType === 'boolean') {
            missingProps[key] = { type: 'boolean' };
          } else if (valueType === 'number') {
            missingProps[key] = { type: 'number' };
          } else if (valueType === 'string') {
            missingProps[key] = { type: 'string' };
          }
        }
      });

      if (Object.keys(missingProps).length > 0) {
        normalized.properties = { ...existingProps, ...missingProps };
      }
    }

    return normalized;
  }, [schema, formData, newDomain, variantValuesKey]);

  const requiredFields = new Set(normalizedSchema.required || []);

  // Fetches the tenant state data by tenant ID using the useRestApi hook.
  const { data: tenantStateDataById } = useRestApi<TenantStateData>(
    apiUrl('tenancy', `tenants/${tenantId}/state`),
  );

  const [, setShowErrors] = React.useState(false);

  // Called if we tried to create or the user cancelled out of the dialog.
  const close = React.useCallback(() => {
    setShowErrors(false);
    setShouldDecryptValues(false);
    hasFetchedDecrypted.current = false;
    onClose();
  }, [onClose]);

  function update(s: any, event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (newDomain) {
      const body = {
        configuration: s.formData,
        tenantId,
        domain: selectedDomain,
      };

      doPost(body);
    } else {
      let siteSpecificConfig: Record<string, any> = {};
      if (fleetConfigDomainData && tenantType.startsWith('Learn')) {
        siteSpecificConfig = Object.keys(s.formData).reduce(
          (acc, key) => {
            const formValue = s.formData[key];
            const fleetValue = fleetConfigDomainData?.configuration[key];

            if (
              requiredFields.has(key) ||
              !Object.prototype.hasOwnProperty.call(fleetConfigDomainData?.configuration, key) ||
              !isEqual(formValue, fleetValue)
            ) {
              acc[key] = formValue;
            }

            return acc;
          },
          {} as Record<string, any>,
        );
      } else {
        siteSpecificConfig = { ...s.formData };
      }

      const body = {
        configuration: siteSpecificConfig,
      };

      doPut(selectedDomain, body);
    }
  }

  function restoreFleetDefaults() {
    let siteOnlyConfig: Record<string, any> = {};

    if (formData && fleetConfigDomainData) {
      const fleetKeys = new Set(Object.keys(fleetConfigDomainData.configuration || {}));

      siteOnlyConfig = Object.keys(formData).reduce(
        (acc, key) => {
          if (requiredFields.has(key) || !fleetKeys.has(key)) {
            acc[key] = formData[key];
          }
          return acc;
        },
        {} as Record<string, any>,
      );
    }

    const body = {
      configuration: siteOnlyConfig,
    };

    doPut(selectedDomain, body);
  }

  React.useEffect(() => {
    if (shouldDecryptValues && !hasFetchedDecrypted.current) {
      decryptConfigFetch();
      hasFetchedDecrypted.current = true;
    }
  }, [shouldDecryptValues, decryptConfigFetch]);

  React.useEffect(() => {
    setConfigData(shouldDecryptValues ? decryptedFormData.configuration || {} : formData || {});
  }, [shouldDecryptValues, decryptedFormData, formData]);

  React.useEffect(() => {
    if (succeededRequests.length > 0) {
      enqueueSnackbar(t('siteConfiguration.updateSucceeded'), {
        variant: 'success',
      });
      clearSucceededRequests();
      close();
      onSuccess();
    }
  }, [clearSucceededRequests, onSuccess, close, enqueueSnackbar, succeededRequests.length, t]);

  // If there was an error creating, show a snackbar notification. We close
  // because otherwise the snackbar will appear under the modal overlay, which
  // is difficult to see. We should revisit this.

  React.useEffect(() => {
    if (failedRequests.length > 0) {
      enqueueSnackbar(
        t('siteConfiguration.updateFailed', {
          message: failedRequests[0].error.message,
        }),
        { variant: 'error' },
      );
      clearFailedRequests();
      close();
    }
  }, [clearFailedRequests, close, enqueueSnackbar, failedRequests, failedRequests.length, t]);

  let content: React.ReactElement;
  let restoreButton: React.ReactElement | null = null;
  if (error) {
    content = (
      <ErrorMessage
        data-testid="config-schema-error"
        title={t('global.fetchError')}
        variant="block"
      />
    );
  } else if (loading || loadingRequests || !tenantStateDataById) {
    content = <LoadingIndicator data-testid="config-schema-loading" />;
  } else {
    const isAvailable: boolean = tenantStateDataById?.Locked === false;
    const helpUiSchema: Record<string, any> = {};
    const fleetValuesInSchema = fleetConfigDomainData?.configuration
      ? Object.keys(fleetConfigDomainData.configuration).reduce(
          (acc, key) => {
            if (
              Object.prototype.hasOwnProperty.call(schema.properties, key) ||
              Object.prototype.hasOwnProperty.call(variantByKey, key)
            ) {
              acc[key] = fleetConfigDomainData.configuration[key];
            }
            return acc;
          },
          {} as Record<string, any>,
        )
      : {};

    if (configData && fleetConfigDomainData) {
      const allFieldNames = new Set([
        ...Object.keys(configData),
        ...Object.keys(fleetValuesInSchema),
      ]);

      allFieldNames.forEach((fieldName) => {
        const siteValues = Object.prototype.hasOwnProperty.call(configData, fieldName);
        const fleetValues = Object.prototype.hasOwnProperty.call(fleetValuesInSchema, fieldName);

        if (siteValues) {
          helpUiSchema[fieldName] = {
            'ui:help': `Source: Site`,
          };
        } else if (fleetValues && !newDomain) {
          helpUiSchema[fieldName] = {
            'ui:help': variantByKey[fieldName] ? (
              <>
                Source: Fleet
                {variantChip(variantByKey[fieldName])}
              </>
            ) : (
              `Source: Fleet`
            ),
          };
        }
      });
    }
    restoreButton =
      fleetConfigDomainData?.configuration &&
      isAvailable &&
      !newDomain &&
      canRestoreToFleetConfiguration &&
      tenantType !== 'Fleet' ? (
        <DefaultButton onClick={() => setFleetRevertConfirmationOpen(true)}>
          {t('siteConfiguration.restoreToFleetDefaults')}
        </DefaultButton>
      ) : null;

    const uiOrder = uiSchema?.['ui:order'];
    const updatedUiSchema = {
      ...helpUiSchema,
      ...uiSchema,
      ...(Array.isArray(uiOrder) && !uiOrder.includes('*')
        ? { 'ui:order': [...uiOrder, '*'] }
        : {}),
      'ui:submitButtonOptions': {
        norender: !isAvailable || (selectedDomain.endsWith('-fleet') && !newDomain),
      },
    };

    const mergedFormData = newDomain
      ? {
          ...(configData || {}),
        }
      : {
          ...(fleetValuesInSchema || {}),
          ...(configData || {}),
        };

    content = (
      <>
        {loadingAutofill && newDomain && (
          <Typography variant="body2" color="error">
            {t('siteConfiguration.loadingAutofillMessage')}
          </Typography>
        )}
        {!isAvailable && (
          <Banner severity="info">
            {t('learnSiteInformation.notAvailableStateModalInformation')}
          </Banner>
        )}
        {fleetValuesInSchema && isAvailable && !newDomain && (
          <Banner severity="info">{t('learnSiteInformation.fleetConfigInformation')}</Banner>
        )}
        <Form
          schema={normalizedSchema}
          formData={mergedFormData}
          onSubmit={update}
          uiSchema={updatedUiSchema}
          disabled={!isAvailable}
          validator={validator}
        />
      </>
    );
  }

  const handleConfirmSubmit = () => {
    restoreFleetDefaults();
    setFleetRevertConfirmationOpen(false);
    close();
  };

  const handleCancelSubmit = () => {
    setFleetRevertConfirmationOpen(false);
  };

  const switchLabelText = shouldDecryptValues
    ? t('siteConfiguration.decryptValues')
    : t('siteConfiguration.encryptValues');
  const tooltipText = shouldDecryptValues
    ? t('siteConfiguration.encryptTooltip')
    : t('siteConfiguration.decryptTooltip');

  const encryptDecryptSwitch =
    canRestoreToFleetConfiguration && !newDomain ? (
      <Tooltip title={tooltipText}>
        <FormControlLabel
          className={classes.switchLabel}
          control={
            <Switch
              id="encrypt-decrypt-switch"
              data-testid="encrypt-decrypt-toggle"
              disableRipple
              checked={shouldDecryptValues}
              classes={{
                track: classes.track,
                switchBase: classes.switchBase,
                checked: classes.checked,
              }}
              icon={
                <span className={classes.iconWrapper}>
                  <Invisible />
                </span>
              }
              checkedIcon={
                <span className={classes.iconWrapper}>
                  <Visible />
                </span>
              }
              onChange={() => setShouldDecryptValues(!shouldDecryptValues)}
            />
          }
          label={switchLabelText}
          htmlFor="encrypt-decrypt-switch"
        />
      </Tooltip>
    ) : null;

  return (
    <>
      <Dialog
        id={id}
        open={open}
        onClose={close}
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
      >
        <DialogTitle
          onClose={close}
          id={`${id}-title`}
          className={classes.dialogTitle}
          closeLabel={t('global.close')}
        >
          {t('siteConfiguration.title')}
          {encryptDecryptSwitch}
        </DialogTitle>
        <DialogContent>{content}</DialogContent>
        {restoreButton}
      </Dialog>

      <Dialog
        open={fleetRevertConfirmationOpen}
        onClose={handleCancelSubmit}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, .75)' },
        }}
      >
        <DialogTitle onClose={handleCancelSubmit} closeLabel={t('global.close')}>
          {t('siteConfiguration.restoreChangesConfirmation')}
        </DialogTitle>
        <DialogContent>{t('siteConfiguration.restoreInfoMessage')}</DialogContent>
        <DialogActions>
          <DefaultButton onClick={handleCancelSubmit}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton onClick={handleConfirmSubmit}>{t('global.submit')}</PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActionsUpdateConfigurationDialog;
